/**
 * RaffleWheel — premium casino-style prize wheel (canvas, no assets).
 * The wheel NEVER decides the winner — the backend picks & saves it first,
 * the wheel only animates landing on `winnerIndex`.
 */
import { useEffect, useRef, useState } from "react";
import { Volume2, VolumeX } from "lucide-react";

// Composite easing: dramatic easeOutQuint deceleration + tiny end wobble for
// the ratchet-settle feel of a real wheel of fortune.
const easeSpin = (t) => {
  const eased = 1 - Math.pow(1 - t, 5);
  if (t > 0.92) {
    const wobT = (t - 0.92) / 0.08;
    const wobble = Math.sin(wobT * Math.PI * 4) * (1 - wobT) * 0.003;
    return eased + wobble;
  }
  return eased;
};

export default function RaffleWheel({
  entrants = [],
  winnerIndex = null,
  spinning = false,
  onSpinComplete = () => {},
  size = 560,
}) {
  const canvasRef = useRef(null);
  const rafRef = useRef(null);
  const rotRef = useRef(0);
  const spinRef = useRef(null); // { start, duration, from, to, windup }
  const lastSegRef = useRef(-1);
  const doneRef = useRef(false);
  const pointerKickRef = useRef(0); // seconds since last segment cross — drives pointer bounce
  const logoImgRef = useRef(null);
  const [muted, setMuted] = useState(() => localStorage.getItem("nm_raffle_muted") === "1");
  const mutedRef = useRef(muted);
  const audioRef = useRef(null);

  useEffect(() => { mutedRef.current = muted; }, [muted]);

  // Preload the site logo (same one used in the nav + cursor) so it can be
  // drawn in the center hub without a flash.
  useEffect(() => {
    const img = new Image();
    img.decoding = "async";
    img.src = "/logo.png?v=4";
    img.onload = () => { logoImgRef.current = img; };
    return () => { img.onload = null; };
  }, []);

  const n = Math.max(entrants.length, 1);
  const hasEntries = entrants.length > 0;
  const showLabels = hasEntries && entrants.length <= 36;

  const playTick = () => {
    if (mutedRef.current) return;
    try {
      if (!audioRef.current) audioRef.current = new (window.AudioContext || window.webkitAudioContext)();
      const ctx = audioRef.current;
      if (ctx.state === "suspended") ctx.resume();
      const osc = ctx.createOscillator();
      const gain = ctx.createGain();
      osc.type = "square";
      osc.frequency.value = 1700;
      gain.gain.setValueAtTime(0.05, ctx.currentTime);
      gain.gain.exponentialRampToValueAtTime(0.0001, ctx.currentTime + 0.045);
      osc.connect(gain);
      gain.connect(ctx.destination);
      osc.start();
      osc.stop(ctx.currentTime + 0.05);
    } catch (_) { /* ignore */ }
  };

  const playFanfare = () => {
    if (mutedRef.current) return;
    try {
      if (!audioRef.current) audioRef.current = new (window.AudioContext || window.webkitAudioContext)();
      const ctx = audioRef.current;
      if (ctx.state === "suspended") ctx.resume();
      [523.25, 659.25, 783.99, 1046.5].forEach((f, i) => {
        const osc = ctx.createOscillator();
        const gain = ctx.createGain();
        osc.type = "triangle";
        osc.frequency.value = f;
        const t0 = ctx.currentTime + i * 0.14;
        gain.gain.setValueAtTime(0.0001, t0);
        gain.gain.exponentialRampToValueAtTime(0.12, t0 + 0.03);
        gain.gain.exponentialRampToValueAtTime(0.0001, t0 + 0.6);
        osc.connect(gain);
        gain.connect(ctx.destination);
        osc.start(t0);
        osc.stop(t0 + 0.65);
      });
    } catch (_) { /* ignore */ }
  };

  // Trigger spin
  useEffect(() => {
    if (!spinning || winnerIndex == null || winnerIndex < 0 || spinRef.current || doneRef.current) return;
    const seg = (Math.PI * 2) / n;
    const current = rotRef.current % (Math.PI * 2);
    // winner segment center lands under the top pointer when rot ≡ -(i+0.5)*seg (mod 2π)
    const jitter = (Math.random() - 0.5) * seg * 0.6;
    const desired = ((Math.PI * 2) - ((winnerIndex + 0.5) * seg) + jitter) % (Math.PI * 2);
    const turns = 8 + Math.floor(Math.random() * 4);
    const delta = ((desired - current) % (Math.PI * 2) + Math.PI * 2) % (Math.PI * 2);
    spinRef.current = {
      start: performance.now(),
      duration: 13000 + Math.random() * 2000,
      windupMs: 550, // brief reverse "anticipation" tug before launch
      windupBack: seg * 0.42, // how far back to pull
      from: rotRef.current,
      to: rotRef.current + turns * Math.PI * 2 + delta,
    };
  }, [spinning, winnerIndex, n]);

  // Render loop
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d");
    const dpr = Math.min(window.devicePixelRatio || 1, 2);
    canvas.width = size * dpr;
    canvas.height = size * dpr;
    canvas.style.width = "100%";
    canvas.style.maxWidth = `${size}px`;
    ctx.setTransform(dpr, 0, 0, dpr, 0, 0);

    const cx = size / 2;
    const cy = size / 2;
    const R = size / 2 - 26;
    const seg = (Math.PI * 2) / n;

    const draw = (rot, time, velocity = 0) => {
      ctx.clearRect(0, 0, size, size);

      // Velocity-driven ambient glow — grows brighter during high-speed spin
      const speedNorm = Math.min(1, velocity / 0.28);
      const outerGlow = ctx.createRadialGradient(cx, cy, R * 0.55, cx, cy, R + 44);
      outerGlow.addColorStop(0, "rgba(0,0,0,0)");
      outerGlow.addColorStop(0.6, `rgba(255,220,120,${0.03 + speedNorm * 0.09})`);
      outerGlow.addColorStop(1, `rgba(255,255,255,${0.05 + speedNorm * 0.12})`);
      ctx.fillStyle = outerGlow;
      ctx.beginPath();
      ctx.arc(cx, cy, R + 44, 0, Math.PI * 2);
      ctx.fill();

      // Motion trail: render 2 additional dimmer wheel copies at slightly
      // earlier rotations when spinning fast — creates smeared blur effect.
      const drawWheelBody = (rotation, alpha) => {
        ctx.save();
        ctx.globalAlpha = alpha;
        for (let i = 0; i < n; i++) {
          const a0 = rotation + i * seg - Math.PI / 2;
          const a1 = a0 + seg;
          ctx.beginPath();
          ctx.moveTo(cx, cy);
          ctx.arc(cx, cy, R, a0, a1);
          ctx.closePath();
          const shade = i % 2 === 0 ? "#161616" : "#1f1f1f";
          const grad = ctx.createRadialGradient(cx, cy, R * 0.15, cx, cy, R);
          grad.addColorStop(0, i % 2 === 0 ? "#242424" : "#2d2d2d");
          grad.addColorStop(1, shade);
          ctx.fillStyle = hasEntries ? grad : "#141414";
          ctx.fill();
          if (n <= 200 && alpha > 0.85) {
            ctx.strokeStyle = "rgba(255,255,255,0.14)";
            ctx.lineWidth = 1;
            ctx.stroke();
          }
        }
        ctx.restore();
      };

      // Motion blur trail during high-velocity spin (skip when static/slow)
      if (speedNorm > 0.35) {
        drawWheelBody(rot - velocity * 0.6, 0.18 * speedNorm);
        drawWheelBody(rot - velocity * 0.3, 0.28 * speedNorm);
      }

      // Main wheel body
      drawWheelBody(rot, 1);

      // Labels (only on the main frame — trails skip them for perf + clarity)
      if (showLabels) {
        for (let i = 0; i < n; i++) {
          const a0 = rot + i * seg - Math.PI / 2;
          const mid = a0 + seg / 2;
          ctx.save();
          ctx.translate(cx, cy);
          ctx.rotate(mid);
          ctx.textAlign = "right";
          ctx.textBaseline = "middle";
          const name = String(entrants[i] || "").slice(0, 14);
          const fs = Math.max(9, Math.min(15, 300 / n + 6));
          ctx.font = `600 ${fs}px 'Manrope', sans-serif`;
          ctx.fillStyle = `rgba(255,255,255,${0.88 - speedNorm * 0.35})`;
          ctx.shadowColor = "rgba(0,0,0,0.8)";
          ctx.shadowBlur = 4;
          ctx.fillText(name, R - 18, 0);
          ctx.restore();
        }
      }

      if (!hasEntries) {
        ctx.textAlign = "center";
        ctx.textBaseline = "middle";
        ctx.font = "600 14px 'JetBrains Mono', monospace";
        ctx.fillStyle = "rgba(255,255,255,0.4)";
        ctx.fillText("AWAITING ENTRIES", cx, cy - R * 0.55);
      }

      // Gloss highlight
      const gloss = ctx.createLinearGradient(cx, cy - R, cx, cy);
      gloss.addColorStop(0, "rgba(255,255,255,0.10)");
      gloss.addColorStop(1, "rgba(255,255,255,0)");
      ctx.beginPath();
      ctx.arc(cx, cy, R, Math.PI, Math.PI * 2);
      ctx.closePath();
      ctx.fillStyle = gloss;
      ctx.fill();

      // Metallic rim
      const rim = ctx.createLinearGradient(cx - R, cy - R, cx + R, cy + R);
      rim.addColorStop(0, "#6b6b6b");
      rim.addColorStop(0.3, "#f5f5f5");
      rim.addColorStop(0.55, "#7a7a7a");
      rim.addColorStop(0.8, "#e8e8e8");
      rim.addColorStop(1, "#5a5a5a");
      ctx.beginPath();
      ctx.arc(cx, cy, R + 8, 0, Math.PI * 2);
      ctx.lineWidth = 10;
      ctx.strokeStyle = rim;
      ctx.stroke();
      ctx.beginPath();
      ctx.arc(cx, cy, R + 1, 0, Math.PI * 2);
      ctx.lineWidth = 2;
      ctx.strokeStyle = "rgba(0,0,0,0.7)";
      ctx.stroke();

      // Rim lamps — chase speed proportional to wheel velocity
      const lamps = 28;
      const chaseSpeed = spinRef.current ? 90 + speedNorm * 220 : 0;
      for (let i = 0; i < lamps; i++) {
        const a = (i / lamps) * Math.PI * 2 + (chaseSpeed ? time / (500 - chaseSpeed) : 0);
        const lx = cx + Math.cos(a) * (R + 8);
        const ly = cy + Math.sin(a) * (R + 8);
        const on = spinRef.current ? (Math.floor(time / (140 - speedNorm * 90)) + i) % 2 === 0 : i % 2 === 0;
        ctx.beginPath();
        ctx.arc(lx, ly, 3, 0, Math.PI * 2);
        ctx.fillStyle = on ? "#ffe9a8" : "rgba(255,255,255,0.25)";
        ctx.shadowColor = on ? "rgba(255,215,120,0.9)" : "transparent";
        ctx.shadowBlur = on ? 10 : 0;
        ctx.fill();
        ctx.shadowBlur = 0;
      }

      // Center hub
      const hub = ctx.createRadialGradient(cx - 8, cy - 8, 4, cx, cy, 50);
      hub.addColorStop(0, "#3a3a3a");
      hub.addColorStop(1, "#0c0c0c");
      ctx.beginPath();
      ctx.arc(cx, cy, 48, 0, Math.PI * 2);
      ctx.fillStyle = hub;
      ctx.fill();
      ctx.lineWidth = 2;
      ctx.strokeStyle = "rgba(255,255,255,0.35)";
      ctx.stroke();
      // Draw the site logo (stays static — does NOT rotate with the wheel)
      if (logoImgRef.current) {
        const logoSize = 56;
        ctx.save();
        ctx.beginPath();
        ctx.arc(cx, cy, 46, 0, Math.PI * 2);
        ctx.clip();
        // Subtle glow behind the logo when the wheel is at rest / winner shown
        if (!spinRef.current && doneRef.current) {
          ctx.shadowColor = "rgba(230,184,76,0.7)";
          ctx.shadowBlur = 16;
        }
        ctx.drawImage(logoImgRef.current, cx - logoSize / 2, cy - logoSize / 2, logoSize, logoSize);
        ctx.restore();
      } else {
        // Fallback until the logo image finishes loading
        ctx.textAlign = "center";
        ctx.textBaseline = "middle";
        ctx.font = "700 26px 'Bebas Neue', sans-serif";
        ctx.fillStyle = "rgba(255,255,255,0.92)";
        ctx.fillText("NM", cx, cy + 2);
      }

      // Fixed pointer (top) — gold, with tick-bounce animation
      // Bounce lasts ~180ms after each segment crossing.
      const sinceKick = time - pointerKickRef.current;
      const kick = pointerKickRef.current > 0 && sinceKick < 180
        ? Math.sin((sinceKick / 180) * Math.PI) * 5
        : 0;
      ctx.save();
      ctx.translate(0, kick);
      ctx.shadowColor = "rgba(230,184,76,0.7)";
      ctx.shadowBlur = 14;
      const pg = ctx.createLinearGradient(cx - 16, 0, cx + 16, 0);
      pg.addColorStop(0, "#d4a437");
      pg.addColorStop(0.5, "#f7e39b");
      pg.addColorStop(1, "#c8962e");
      ctx.fillStyle = pg;
      ctx.beginPath();
      ctx.moveTo(cx - 16, cy - R - 18);
      ctx.lineTo(cx + 16, cy - R - 18);
      ctx.lineTo(cx, cy - R + 16);
      ctx.closePath();
      ctx.fill();
      ctx.lineWidth = 1.5;
      ctx.strokeStyle = "rgba(0,0,0,0.6)";
      ctx.stroke();
      ctx.restore();
    };

    const loop = (time) => {
      let velocity = 0;
      if (spinRef.current) {
        const s = spinRef.current;
        const prev = rotRef.current;
        const elapsed = time - s.start;
        if (elapsed < s.windupMs) {
          // Anticipation tug — small backward pull with easeInOut
          const wt = elapsed / s.windupMs;
          const eased = wt < 0.5 ? 2 * wt * wt : 1 - Math.pow(-2 * wt + 2, 2) / 2;
          rotRef.current = s.from - s.windupBack * eased;
        } else {
          const t = Math.min(1, (elapsed - s.windupMs) / s.duration);
          // Launch from the wound-up position so it snaps forward
          const launchFrom = s.from - s.windupBack;
          const launchTo = s.to;
          rotRef.current = launchFrom + (launchTo - launchFrom) * easeSpin(t);
          // tick on segment boundary crossings (skip during windup)
          const segIdx = Math.floor(((rotRef.current % (Math.PI * 2)) + Math.PI * 2) % (Math.PI * 2) / seg);
          if (segIdx !== lastSegRef.current) {
            lastSegRef.current = segIdx;
            pointerKickRef.current = time;
            if (t < 0.985) playTick();
          }
          if (t >= 1) {
            spinRef.current = null;
            doneRef.current = true;
            playFanfare();
            onSpinComplete();
          }
        }
        velocity = Math.abs(rotRef.current - prev);
      } else if (!doneRef.current) {
        const prev = rotRef.current;
        rotRef.current += 0.0018; // slow idle drift
        velocity = Math.abs(rotRef.current - prev);
      }
      draw(rotRef.current, time, velocity);
      rafRef.current = requestAnimationFrame(loop);
    };
    rafRef.current = requestAnimationFrame(loop);
    return () => cancelAnimationFrame(rafRef.current);
  }, [entrants, n, size, showLabels, hasEntries]);

  const toggleMute = () => {
    setMuted((m) => {
      localStorage.setItem("nm_raffle_muted", m ? "0" : "1");
      return !m;
    });
  };

  return (
    <div className="relative inline-block w-full max-w-[560px]" data-testid="raffle-wheel">
      <canvas ref={canvasRef} className="w-full h-auto block" />
      <button
        type="button"
        data-testid="raffle-wheel-mute"
        onClick={toggleMute}
        aria-label={muted ? "Unmute wheel sounds" : "Mute wheel sounds"}
        title={muted ? "Unmute" : "Mute"}
        className="absolute bottom-2 right-2 w-10 h-10 flex items-center justify-center rounded-full border border-[var(--theme-border)] bg-black/70 backdrop-blur text-[var(--theme-text-muted)] hover:text-[var(--theme-text)] hover:border-[var(--theme-accent-primary)]/60 transition-colors"
      >
        {muted ? <VolumeX size={16} /> : <Volume2 size={16} />}
      </button>
    </div>
  );
}
